<script setup>
import fiveMixin from '@/composables/fiveMixin'
import useFunctions from '@/composables/useFunctions'
import { DEV_MODE } from '@/config/dev-mode'

import IconPeople from '@/components/svg/IconPeople.vue'

const { ...five } = fiveMixin()
const { ...useFun } = useFunctions()
const props = defineProps({
  birthData: {
    type: Object,
    default: null,
  },
  birthEightTxt: {
    type: String,
    default: '',
  },
  isPrint: {
    type: Boolean,
    default: false,
  },
  rootPage: {
    type: String,
    default: '',
  },
  rootFiveClass: {
    type: String,
    default: '',
  },
  birthDataSkyEarth: {
    type: Array,
    default: [],
  },
  sajugangiKorArray: {
    type: Array,
    default: [],
  },
  selectedDescNavSub: {
    type: String,
    default: 'first',
  },
  currentStep: {
    type: Number,
    default: 0,
  },
  totalSteps: {
    type: Number,
    default: 0,
  },
  isAnimating: {
    type: Boolean,
    default: false,
  },
  langMode: {
    type: String,
    default: 'chnkor',
  },
})
onMounted(() => {})

const isMatch = (char) => {
  return props.birthEightTxt.includes(char)
}

const getChars = (char) => {
  const matchedChars = props.birthEightTxt?.split('').filter((c) => c === char)
  return matchedChars.length > 0 ? matchedChars : [char]
}

// 각 오행별 매칭되는 글자들을 확인하는 함수
const getFiveElementChars = () => {
  if (!props.birthDataSkyEarth || !Array.isArray(props.birthDataSkyEarth)) {
    return {
      thu: [], // 목
      tue: [], // 화
      sat: [], // 토
      fri: [], // 금
      wed: [], // 수
    }
  }

  const fiveElements = {
    thu: [], // 목
    tue: [], // 화
    sat: [], // 토
    fri: [], // 금
    wed: [], // 수
  }

  props.birthDataSkyEarth.forEach((item) => {
    if (item.sky) {
      // 목 (甲, 乙, 寅, 卯)
      if (['甲', '乙', '寅', '卯'].includes(item.sky)) {
        fiveElements.thu.push(item.sky)
      }
      // 화 (丙, 丁, 巳, 午)
      if (['丙', '丁', '巳', '午'].includes(item.sky)) {
        fiveElements.tue.push(item.sky)
      }
      // 토 (戊, 己, 辰, 戌, 丑, 未)
      if (['戊', '己', '辰', '戌', '丑', '未'].includes(item.sky)) {
        fiveElements.sat.push(item.sky)
      }
      // 금 (庚, 辛, 申, 酉)
      if (['庚', '辛', '申', '酉'].includes(item.sky)) {
        fiveElements.fri.push(item.sky)
      }
      // 수 (壬, 癸, 亥, 子)
      if (['壬', '癸', '亥', '子'].includes(item.sky)) {
        fiveElements.wed.push(item.sky)
      }
    }

    if (item.earth) {
      // 지지도 동일하게 확인
      if (['甲', '乙', '寅', '卯'].includes(item.earth)) {
        fiveElements.thu.push(item.earth)
      }
      if (['丙', '丁', '巳', '午'].includes(item.earth)) {
        fiveElements.tue.push(item.earth)
      }
      if (['戊', '己', '辰', '戌', '丑', '未'].includes(item.earth)) {
        fiveElements.sat.push(item.earth)
      }
      if (['庚', '辛', '申', '酉'].includes(item.earth)) {
        fiveElements.fri.push(item.earth)
      }
      if (['壬', '癸', '亥', '子'].includes(item.earth)) {
        fiveElements.wed.push(item.earth)
      }
    }
  })

  return fiveElements
}

// 특정 오행에 매칭되는 글자가 있는지 확인하는 함수
const hasFiveElementChar = (elementType) => {
  const fiveElements = getFiveElementChars()
  return fiveElements[elementType].length > 0
}

// 현재 단계와 오행 매칭을 모두 확인하는 함수 (누적 active)
const isStepAndElementActive = (step, elementType) => {
  // 현재 단계가 해당 단계보다 크거나 같고, 해당 오행에 매칭되는 글자가 있으면 active
  return props.currentStep >= step && hasFiveElementChar(elementType)
}

/** 받은 데이터에서 일간 일지 문자 구하기 */
const getDayData = (text) => {
  if (!text) return { sky: { char: '', index: 2 }, earth: { char: '', index: 6 } }
  const data = text.split('')
  return {
    sky: {
      char: data[2],
      index: 2,
    },
    earth: {
      char: data[6],
      index: 6,
    },
  }
}

const skyDayData = ref('')
const earthDayData = ref('')
watch(
  () => props.birthEightTxt,
  (newVal) => {
    const dayData = getDayData(newVal)
    skyDayData.value = dayData.sky
    earthDayData.value = dayData.earth
    DEV_MODE && console.log('일간:', skyDayData.value, '/ 일지:', earthDayData.value)
  },
  { immediate: true },
)

// birthEightTxt에 대한 index
const globalIndex = (localIndex, char) => {
  const data = props.birthEightTxt?.split('')
  let count = 0
  for (let i = 0; i < data.length; i++) {
    if (data[i] === char) {
      if (count === localIndex) {
        return i // 로컬 인덱스에 대응하는 전역 인덱스 반환
      }
      count++
    }
  }
  return -1
}

// skyDay 추상화
const isSkyDay = (char, index, charType, isReversed = false) => {
  const chars = getProcessedChars(charType, isReversed)
  const processedIndex = isReversed ? originalIndex(index, chars.length) : index

  return char === skyDayData.char && processedIndex === skyDayData.index
}

// 역순이 필요한 자리에 넣을 함수
const getProcessedChars = (charType, isReversed = false) => {
  const chars = getChars(charType).map((char, index) => ({ char, index })) // 데이터와 인덱스 묶기
  if (isReversed) {
    return chars.reverse() // 화면 순서 반전
  }
  return chars // 원래 순서
}

// 부모로 이벤트 emit
const emit = defineEmits([
  'skyDay-click',
  'open-ilgan',
  'skyDay-reset',
  'resetSelectedState',
  'openPopFiveStar',
  'closePopFiveStar',
  'clickIlganButton',
]) // 부모 컴포넌트에서 'skyDay-click'을 받아야 함

const CJClickedIdx = ref(null)
const selectedChar = ref(null)
const selectedIndex = ref(null)
const updateClickIdx = (char, index) => {
  selectedChar.value = char
  CJClickedIdx.value = index
}
/** 천간, 지지 클릭시 */
const handleClick = (day, char, index) => {
  if (props.isPrint) {
    return
  }
  // five pro top component에 표시 해제
  if (CJClickedIdx.value === index && selectedChar.value === char) {
    CJClickedIdx.value = null
    selectedChar.value = null
    emit('skyDay-reset')
    return
  }
  updateClickIdx(char, index) // 뒷 배경 표시
  // five pro top component에 표시
  const globalIdx = globalIndex(index, char)
  const isSkyDayClicked = char === skyDayData.value.char && globalIdx === skyDayData.value.index
  const whichDay = day
  emit('skyDay-click', {
    whichDay,
    isSkyDayClicked,
    globalIdx,
  })
  DEV_MODE && console.log('star컴포넌트에서 클릭된 데이터', char, whichDay, globalIdx)
  if (isSkyDayClicked) {
    // 일간 클릭했을 경우 팝업 띄우기
    emit('open-ilgan', { char, whichDay, globalIdx })
  } else {
    emit('close-ilgan')
  }
}

// 격국
const isGyeokClicked = ref(false)
const openGyeokguk = () => {
  isGyeokClicked.value = true
}
const closeGyeokguk = () => {
  isGyeokClicked.value = false
}

// 용신
const isYongClicked = ref(false)
const openYongsin = () => {
  isYongClicked.value = true
}
const closeYongsin = () => {
  isYongClicked.value = false
}

const resetSelectedState = () => {
  CJClickedIdx.value = null
  selectedChar.value = null
  // emit('skyDay-reset');
}

const clickPopFiveStarButton = () => {
  emit('openPopFiveStar')
}

const clickIlganButton = () => emit('clickIlganButton')

// birthDataSkyEarth 데이터에서 sky와 earth 값들을 추출
const getMatchedChars = () => {
  if (!props.birthDataSkyEarth || !Array.isArray(props.birthDataSkyEarth)) {
    return { skyChars: [], earthChars: [] }
  }

  const skyChars = props.birthDataSkyEarth.map((item) => item.sky).filter(Boolean)
  const earthChars = props.birthDataSkyEarth.map((item) => item.earth).filter(Boolean)

  return { skyChars, earthChars }
}

// 특정 한자가 매칭되는지 확인하는 함수
const isCharMatched = (char, type = 'sky') => {
  const { skyChars, earthChars } = getMatchedChars()
  const targetChars = type === 'sky' ? skyChars : earthChars
  return targetChars.includes(char)
}

// 2~4개의 글자가 매칭되는지 확인하는 함수
const isMultipleCharsMatched = (chars, type = 'sky') => {
  const { skyChars, earthChars } = getMatchedChars()
  const targetChars = type === 'sky' ? skyChars : earthChars

  // chars 배열의 글자들 중에서 targetChars에 포함된 것들의 개수를 확인
  const matchedCount = chars.filter((char) => targetChars.includes(char)).length

  // 2개 이상 4개 이하인지 확인
  return matchedCount >= 1 && matchedCount <= 4
}

// birthData.five에서 num > 0인 항목들을 추출하는 computed 함수
const getActiveFiveClasses = computed(() => {
  if (!props.birthData?.five) {
    return []
  }

  const activeClasses = []
  Object.keys(props.birthData.five).forEach((key) => {
    const fiveItem = props.birthData.five[key]
    if (fiveItem && fiveItem.num > 0) {
      activeClasses.push(key)
    }
  })

  return activeClasses
})

// 특정 한자에 매칭되는 pos 값을 반환하는 함수
const getCharPos = (char, type = 'sky') => {
  if (!props.birthDataSkyEarth || !Array.isArray(props.birthDataSkyEarth)) {
    return null
  }

  const matchedItem = props.birthDataSkyEarth.find((item) => {
    if (type === 'sky') {
      return item.sky === char
    } else {
      return item.earth === char
    }
  })

  if (matchedItem) {
    // pos 값에 '간' 또는 '지'를 붙여서 반환
    if (type === 'sky') {
      return matchedItem.pos + '간'
    } else {
      return matchedItem.pos + '지'
    }
  }

  return null
}

// pos 값이 '일'인 데이터인지 확인하는 함수 (일간 표시용)
const isIlgan = (char, type = 'sky') => {
  if (!props.birthDataSkyEarth || !Array.isArray(props.birthDataSkyEarth)) {
    return false
  }

  const matchedItem = props.birthDataSkyEarth.find((item) => {
    if (type === 'sky') {
      return item.sky === char
    } else {
      return item.earth === char
    }
  })

  // 일간은 earth에서 pos가 '일'인 것을 찾아야 함
  return matchedItem && matchedItem.pos === '일'
}

// 한자에 동적 클래스명 적용 (애니메이션과 연동)
const getCharStyle = (char, type = 'sky', day = 'fri', section = '') => {
  // 오행별 단계 매핑
  const dayStepMap = {
    thu: 1, // 목 - 1단계
    tue: 2, // 화 - 2단계
    sat: 3, // 토 - 3단계
    fri: 4, // 금 - 4단계
    wed: 5, // 수 - 5단계
  }

  const currentStep = dayStepMap[day] || 0

  // selectedDescNavSub가 'third'이고 section이 'bottom'이면 char-main에서 active 제거
  if (props.selectedDescNavSub === 'third' && section === 'bottom') {
    return 'normal'
  }

  if (props.selectedDescNavSub === 'fourth' && section === 'top') {
    return 'normal'
  }

  // 현재 단계가 해당 오행 단계보다 크거나 같고, 해당 글자가 매칭되면 active
  if (props.currentStep >= currentStep && isCharMatched(char, type)) {
    return 'active'
  }
  return 'normal'
}

// char-3용 클래스명 반환 함수 (icon-people 포함)
const getChar3Style = (char, type = 'sky', day = 'fri', section = '') => {
  const baseStyle = getCharStyle(char, type, day, section)
  const isIlganChar = isIlgan(char, type)

  return {
    [baseStyle]: true,
    'icon-people': isIlganChar && type === 'sky',
  }
}

// 현재 글자가 active 상태인지 여부
const isCharActive = (char, type = 'sky', day = 'fri', section = '') => {
  return getCharStyle(char, type, day, section) === 'active'
}

// 공통: 한자 -> 오행/이름 매핑과 툴팁 생성기
const elementInfo = {
  木: { ko: '목', hanja: '木' },
  火: { ko: '화', hanja: '火' },
  土: { ko: '토', hanja: '土' },
  金: { ko: '금', hanja: '金' },
  水: { ko: '수', hanja: '水' },
}
const charToElement = {
  // 천간 10자
  甲: '木',
  乙: '木',
  丙: '火',
  丁: '火',
  戊: '土',
  己: '土',
  庚: '金',
  辛: '金',
  壬: '水',
  癸: '水',
  // 지지 12자
  寅: '木',
  卯: '木',
  巳: '火',
  午: '火',
  辰: '土',
  戌: '土',
  丑: '土',
  未: '土',
  申: '金',
  酉: '金',
  亥: '水',
  子: '水',
}
const charKoName = {
  // 천간
  甲: '갑목',
  乙: '을목',
  丙: '병화',
  丁: '정화',
  戊: '무토',
  己: '기토',
  庚: '경금',
  辛: '신금',
  壬: '임수',
  癸: '계수',
  // 지지
  寅: '인목',
  卯: '묘목',
  巳: '사화',
  午: '오화',
  辰: '진토',
  戌: '술토',
  丑: '축토',
  未: '미토',
  申: '신금',
  酉: '유금',
  亥: '해수',
  子: '자수',
}

// 천간과 지지 구분된 첫 번째 글자만 있는 객체
const charKorName = {
  // 천간 (天干)
  sky: {
    甲: '갑',
    乙: '을',
    丙: '병',
    丁: '정',
    戊: '무',
    己: '기',
    庚: '경',
    辛: '신',
    壬: '임',
    癸: '계',
  },
  // 지지 (地支)
  earth: {
    寅: '인',
    卯: '묘',
    巳: '사',
    午: '오',
    辰: '진',
    戌: '술',
    丑: '축',
    未: '미',
    申: '신',
    酉: '유',
    亥: '해',
    子: '자',
  },
}
const getTooltipText = (char) => {
  const elementKey = charToElement[char]
  if (!elementKey) return ''
  const element = elementInfo[elementKey]
  const nameKo = charKoName[char] || ''
  return `${element.ko}의 기운\n${char}${element.hanja}(${nameKo})`
}

// langMode에 따라 표시할 텍스트를 반환하는 함수
const getDisplayText = (char, type = 'char', charType = 'sky') => {
  if (props.langMode === 'chn') {
    // 한자만 표시
    return char
  } else if (props.langMode === 'kor') {
    // 한글만 표시 - 천간/지지 구분된 첫 번째 글자
    return charKorName[charType]?.[char] || char
  } else {
    // 기본값: 한자/한글 모두 표시
    if (type === 'char') {
      return char
    } else if (type === 'name') {
      // 한글 이름 표시 (천간/지지 구분된 첫 번째 글자)
      return charKorName[charType]?.[char] || char
    }
  }
  return char
}

defineExpose({
  resetSelectedState,
})
</script>
<template>
  <div class="pro-star-box myfive-box" :class="selectedDescNavSub">
    <div
      class="div-back five-back03 st-back-thu"
      :class="{ active: isStepAndElementActive(1, 'thu') }"
    ></div>
    <!-- thu -->
    <div
      class="div-back five-back01 st-back-tue"
      :class="{ active: isStepAndElementActive(2, 'tue') }"
    ></div>
    <!-- tue -->
    <div
      class="div-back five-back05 st-back-sat"
      :class="{ active: isStepAndElementActive(3, 'sat') }"
    ></div>
    <!-- sat -->
    <div
      class="div-back five-back04 st-back-fri"
      :class="{ active: isStepAndElementActive(4, 'fri') }"
    ></div>
    <!-- fri -->
    <div
      class="div-back five-back02 st-back-wed"
      :class="{ active: isStepAndElementActive(5, 'wed') }"
    ></div>
    <!-- wed -->

    <div class="div-txt st-thu" :class="{ active: isStepAndElementActive(1, 'thu') }">木</div>
    <div class="div-txt st-tue" :class="{ active: isStepAndElementActive(2, 'tue') }">火</div>
    <div class="div-txt st-sat" :class="{ active: isStepAndElementActive(3, 'sat') }">土</div>
    <div class="div-txt st-fri" :class="{ active: isStepAndElementActive(4, 'fri') }">金</div>
    <div class="div-txt st-wed" :class="{ active: isStepAndElementActive(5, 'wed') }">水</div>
    <!--클릭/마우스오버 인식 필요해서 레이어 순서 변경-->

    <!-- TODO: 격국 옆 데이터는 받는 데이터 -->
    <!-- <div class="right-btn">
        <button class="gyeok-yong" @click="openGyeokguk()">격국 : 정재격 <div class="arrow"></div></button>
        <button class="gyeok-yong" @click="openYongsin()">용신 <div class="arrow"></div></button>
     </div> -->
    <div class="light-round-box" v-if="selectedDescNavSub === 'fifth'">
      <div class="text-box">양</div>
    </div>
    <div class="dark-round-box" v-if="selectedDescNavSub === 'fifth'">
      <div class="text-box">음</div>
    </div>
    <!-- TODO: 리팩토링? -->
    <!-- 토 -->
    <div class="txt-five-box st-05 st-sat">
      <div class="txt-top" :class="{ twoActive: isMultipleCharsMatched(['戊', '己'], 'sky') }">
        <div class="al-left-box" :class="{ active: isCharMatched('戊', 'sky') }">
          <div class="char-wrapper" :class="{ myActive: isCharMatched('戊', 'sky') }">
            <span class="char-myfive-box">
              <div class="char-main" :class="getCharStyle('戊', 'sky', 'sat', 'top')">
                {{ getDisplayText('戊', 'char', 'sky') }}
              </div>
              <VTooltip
                v-if="isCharActive('戊', 'sky', 'sat', 'top')"
                activator="parent"
                location="top"
                ><div class="nm-tooltip">{{ getTooltipText('戊') }}</div></VTooltip
              >
              <div class="char-1" :class="getChar3Style('戊', 'sky', 'sat', 'top')">무토</div>
              <div class="char-2">양 中 양</div>
              <div
                class="char-3"
                v-if="getCharPos('戊', 'sky')"
                :class="getChar3Style('戊', 'sky', 'sat', 'top')"
              >
                {{ getCharPos('戊', 'sky') }}
              </div>
            </span>
          </div>
        </div>
        <div class="al-right-box" :class="{ active: isCharMatched('己', 'sky') }">
          <div class="char-wrapper" :class="{ myActive: isCharMatched('己', 'sky') }">
            <span class="char-myfive-box">
              <div class="char-main" :class="getCharStyle('己', 'sky', 'sat', 'top')">
                {{ getDisplayText('己', 'char', 'sky') }}
              </div>
              <VTooltip
                v-if="isCharActive('己', 'sky', 'sat', 'top')"
                activator="parent"
                location="top"
                ><div class="nm-tooltip">{{ getTooltipText('己') }}</div></VTooltip
              >
              <div class="char-1" :class="getChar3Style('己', 'sky', 'sat', 'top')">기토</div>
              <div class="char-2">음 中 음</div>
              <div
                class="char-3"
                v-if="getCharPos('己', 'sky')"
                :class="getChar3Style('己', 'sky', 'sat', 'top')"
              >
                {{ getCharPos('己', 'sky') }}
              </div>
            </span>
          </div>
        </div>
      </div>
      <div class="txt-line-box"></div>
      <div
        class="txt-btm"
        :class="{ twoActive: isMultipleCharsMatched(['辰', '戌', '丑', '未'], 'earth') }"
      >
        <div
          class="al-left-box"
          :class="{ active: isCharMatched('辰', 'earth') || isCharMatched('戌', 'earth') }"
        >
          <div class="char-wrapper" :class="{ myActive: isCharMatched('辰', 'earth') }">
            <!-- 일지 표시 -->
            <span class="char-myfive-box">
              <div class="char-main" :class="getCharStyle('辰', 'earth', 'sat', 'bottom')">
                {{ getDisplayText('辰', 'char', 'earth') }}
              </div>
              <VTooltip
                v-if="isCharActive('辰', 'earth', 'sat', 'bottom')"
                activator="parent"
                location="top"
                ><div class="nm-tooltip">{{ getTooltipText('辰') }}</div></VTooltip
              >
              <div class="char-1" :class="getChar3Style('辰', 'earth', 'sat', 'bottom')">진토</div>
              <div class="char-2">양 中 양</div>
              <div
                class="char-3"
                v-if="getCharPos('辰', 'earth')"
                :class="getChar3Style('辰', 'earth', 'sat', 'bottom')"
              >
                {{ getCharPos('辰', 'earth') }}
              </div>
            </span>
          </div>

          <div class="char-wrapper" :class="{ myActive: isCharMatched('戌', 'earth') }">
            <!-- 일지 표시 -->
            <span class="char-myfive-box">
              <div class="char-main" :class="getCharStyle('戌', 'earth', 'sat', 'bottom')">
                {{ getDisplayText('戌', 'char', 'earth') }}
              </div>
              <VTooltip
                v-if="isCharActive('戌', 'earth', 'sat', 'bottom')"
                activator="parent"
                location="top"
                ><div class="nm-tooltip">{{ getTooltipText('戌') }}</div></VTooltip
              >
              <div class="char-1" :class="getChar3Style('戌', 'earth', 'sat', 'bottom')">술토</div>
              <div class="char-2">양 中 음</div>
              <div
                class="char-3"
                v-if="getCharPos('戌', 'earth')"
                :class="getChar3Style('戌', 'earth', 'sat', 'bottom')"
              >
                {{ getCharPos('戌', 'earth') }}
              </div>
            </span>
          </div>
        </div>
        <div
          class="al-right-box"
          :class="{ active: isCharMatched('丑', 'earth') || isCharMatched('미', 'earth') }"
        >
          <div class="char-wrapper" :class="{ myActive: isCharMatched('丑', 'earth') }">
            <!-- 일지 표시 -->
            <span class="char-myfive-box">
              <div class="char-main" :class="getCharStyle('丑', 'earth', 'sat', 'bottom')">
                {{ getDisplayText('丑', 'char', 'earth') }}
              </div>
              <VTooltip
                v-if="isCharActive('丑', 'earth', 'sat', 'bottom')"
                activator="parent"
                location="top"
                ><div class="nm-tooltip">{{ getTooltipText('丑') }}</div></VTooltip
              >
              <div class="char-1" :class="getChar3Style('丑', 'earth', 'sat', 'bottom')">축토</div>
              <div class="char-2">음 中 양</div>
              <div
                class="char-3"
                v-if="getCharPos('丑', 'earth')"
                :class="getChar3Style('丑', 'earth', 'sat', 'bottom')"
              >
                {{ getCharPos('丑', 'earth') }}
              </div>
            </span>
          </div>
          <div class="char-wrapper" :class="{ myActive: isCharMatched('未', 'earth') }">
            <!-- 일지 표시 -->
            <span class="char-myfive-box">
              <div class="char-main" :class="getCharStyle('未', 'earth', 'sat', 'bottom')">
                {{ getDisplayText('未', 'char', 'earth') }}
              </div>
              <VTooltip
                v-if="isCharActive('未', 'earth', 'sat', 'bottom')"
                activator="parent"
                location="top"
                ><div class="nm-tooltip">{{ getTooltipText('未') }}</div></VTooltip
              >
              <div class="char-1" :class="getChar3Style('未', 'earth', 'sat', 'bottom')">미토</div>
              <div class="char-2">음 中 음</div>
              <div
                class="char-3"
                v-if="getCharPos('未', 'earth')"
                :class="getChar3Style('未', 'earth', 'sat', 'bottom')"
              >
                {{ getCharPos('未', 'earth') }}
              </div>
            </span>
          </div>
        </div>
      </div>
    </div>
    <!-- 금 -->
    <div class="txt-five-box st-04 st-fri">
      <div class="txt-top" :class="{ twoActive: isMultipleCharsMatched(['庚', '辛'], 'sky') }">
        <div class="al-left-box" :class="{ active: isCharMatched('庚', 'sky') }">
          <div class="char-wrapper" :class="{ myActive: isCharMatched('庚', 'sky') }">
            <!-- "일간" 표시 -->
            <span class="char-myfive-box">
              <div class="char-main" :class="getCharStyle('庚', 'sky', 'fri', 'top')">
                {{ getDisplayText('庚', 'char', 'sky') }}
              </div>
              <VTooltip
                v-if="isCharActive('庚', 'sky', 'fri', 'top')"
                activator="parent"
                location="top"
                ><div class="nm-tooltip">{{ getTooltipText('庚') }}</div></VTooltip
              >
              <div class="char-1" :class="getChar3Style('庚', 'sky', 'fri', 'top')">경금</div>
              <div class="char-2">음 中 양</div>
              <div
                class="char-3"
                v-if="getCharPos('庚', 'sky')"
                :class="getChar3Style('庚', 'sky', 'fri', 'top')"
              >
                {{ getCharPos('庚', 'sky') }}
              </div>
            </span>
          </div>
        </div>
        <div class="al-right-box" :class="{ active: isCharMatched('辛', 'sky') }">
          <div class="char-wrapper" :class="{ myActive: isCharMatched('辛', 'sky') }">
            <!-- "일간" 표시 -->
            <span class="char-myfive-box">
              <div
                class="char-main"
                :class="getChar3Style('辛', 'sky', 'fri', 'top')"
                @click="handleClick('fri', char, index)"
              >
                {{ getDisplayText('辛', 'char', 'sky') }}
              </div>
              <VTooltip
                v-if="isCharActive('辛', 'sky', 'fri', 'top')"
                activator="parent"
                location="top"
                ><div class="nm-tooltip">{{ getTooltipText('辛') }}</div></VTooltip
              >
              <div class="char-1" :class="getChar3Style('辛', 'sky', 'fri', 'top')">신금</div>
              <div class="char-2">음 中 음</div>
              <div
                class="char-3"
                v-if="getCharPos('辛', 'sky')"
                :class="getChar3Style('辛', 'sky', 'fri', 'top')"
              >
                {{ getCharPos('辛', 'sky') }}
              </div>
            </span>
          </div>
        </div>
      </div>
      <div class="txt-line-box"></div>
      <div class="txt-btm" :class="{ twoActive: isMultipleCharsMatched(['亥', '癸'], 'earth') }">
        <div class="al-left-box" :class="{ active: isCharMatched('亥', 'earth') }">
          <div class="char-wrapper" :class="{ myActive: isCharMatched('亥', 'earth') }">
            <!-- 일지 표시 -->
            <span class="char-myfive-box">
              <div class="char-main" :class="getCharStyle('申', 'earth', 'fri', 'bottom')">
                {{ getDisplayText('申', 'char', 'earth') }}
              </div>
              <VTooltip
                v-if="isCharActive('申', 'earth', 'fri', 'bottom')"
                activator="parent"
                location="top"
                ><div class="nm-tooltip">{{ getTooltipText('申') }}</div></VTooltip
              >
              <div class="char-1" :class="getChar3Style('申', 'earth', 'fri', 'bottom')">신금</div>
              <div class="char-2">음 中 양</div>
              <div
                class="char-3"
                v-if="getCharPos('申', 'earth')"
                :class="getChar3Style('申', 'earth', 'fri', 'bottom')"
              >
                {{ getCharPos('申', 'earth') }}
              </div>
            </span>
          </div>
        </div>
        <div class="al-right-box" :class="{ active: isCharMatched('癸', 'sky') }">
          <div class="char-wrapper" :class="{ myActive: isCharMatched('癸', 'sky') }">
            <!-- 일지 표시 -->
            <span class="char-myfive-box">
              <div
                class="char-main"
                :class="getChar3Style('酉', 'earth', 'fri', 'bottom')"
                @click="handleClick('fri', char, index)"
              >
                {{ getDisplayText('酉', 'char', 'earth') }}
              </div>
              <VTooltip
                v-if="isCharActive('酉', 'earth', 'fri', 'bottom')"
                activator="parent"
                location="top"
                ><div class="nm-tooltip">{{ getTooltipText('酉') }}</div></VTooltip
              >
              <div class="char-1" :class="getChar3Style('酉', 'earth', 'fri', 'bottom')">유금</div>
              <div class="char-2">음 中 음</div>
              <div
                class="char-3"
                v-if="getCharPos('酉', 'earth')"
                :class="getChar3Style('酉', 'earth', 'fri', 'bottom')"
              >
                {{ getCharPos('酉', 'earth') }}
              </div>
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- 수 -->
    <div class="txt-five-box st-02 st-wed">
      <div class="inner-center-box">
        <div class="txt-top" :class="{ twoActive: isMultipleCharsMatched(['壬', '癸'], 'sky') }">
          <div class="al-left-box" :class="{ active: isCharMatched('壬', 'sky') }">
            <div class="char-wrapper" :class="{ myActive: isCharMatched('壬', 'sky') }">
              <!-- "일간" 표시 -->
              <span class="char-myfive-box">
                <div class="char-main" :class="getCharStyle('壬', 'sky', 'wed', 'top')">
                  {{ getDisplayText('壬', 'char', 'sky') }}
                </div>
                <VTooltip
                  v-if="isCharActive('壬', 'sky', 'wed', 'top')"
                  activator="parent"
                  location="top"
                  ><div class="nm-tooltip">{{ getTooltipText('壬') }}</div></VTooltip
                >
                <div class="char-1" :class="getChar3Style('壬', 'sky', 'wed', 'top')">임수</div>
                <div class="char-2">음 中 양</div>
                <div
                  class="char-3"
                  v-if="getCharPos('壬', 'sky')"
                  :class="getChar3Style('壬', 'sky', 'wed', 'top')"
                >
                  {{ getCharPos('壬', 'sky') }}
                </div>
              </span>
            </div>
          </div>
          <div class="al-right-box" :class="{ active: isCharMatched('癸', 'earth') }">
            <div class="char-wrapper" :class="{ myActive: isCharMatched('癸', 'earth') }">
              <!-- "일간" 표시 -->
              <span class="char-myfive-box">
                <div class="char-main" :class="getCharStyle('癸', 'sky', 'wed', 'top')">
                  {{ getDisplayText('癸', 'char', 'sky') }}
                </div>
                <VTooltip
                  v-if="isCharActive('癸', 'sky', 'wed', 'top')"
                  activator="parent"
                  location="top"
                  ><div class="nm-tooltip">{{ getTooltipText('癸') }}</div></VTooltip
                >
                <div class="char-1" :class="getChar3Style('癸', 'sky', 'wed', 'top')">계수</div>
                <div class="char-2">음 中 음</div>
                <div
                  class="char-3"
                  v-if="getCharPos('癸', 'sky')"
                  :class="getChar3Style('癸', 'sky', 'wed', 'top')"
                >
                  {{ getCharPos('癸', 'sky') }}
                </div>
              </span>
            </div>
          </div>
        </div>
        <div class="txt-line-box"></div>
        <div class="txt-btm" :class="{ twoActive: isMultipleCharsMatched(['亥', '子'], 'earth') }">
          <div class="al-left-box" :class="{ active: isCharMatched('亥', 'earth') }">
            <div class="char-wrapper" :class="{ myActive: isCharMatched('亥', 'earth') }">
              <!-- 일지 표시 -->
              <span class="char-myfive-box">
                <div class="char-main" :class="getCharStyle('亥', 'earth', 'wed', 'bottom')">
                  {{ getDisplayText('亥', 'char', 'earth') }}
                </div>
                <VTooltip
                  v-if="isCharActive('亥', 'earth', 'wed', 'bottom')"
                  activator="parent"
                  location="top"
                  ><div class="nm-tooltip">{{ getTooltipText('亥') }}</div></VTooltip
                >
                <div class="char-1" :class="getChar3Style('亥', 'earth', 'wed', 'bottom')">
                  해수
                </div>
                <div class="char-2">음 中 양</div>
                <div
                  class="char-3"
                  v-if="getCharPos('亥', 'earth')"
                  :class="getChar3Style('亥', 'earth', 'wed', 'bottom')"
                >
                  {{ getCharPos('亥', 'earth') }}
                </div>
              </span>
            </div>
          </div>
          <div class="al-right-box" :class="{ active: isCharMatched('子', 'sky') }">
            <div class="char-wrapper" :class="{ myActive: isCharMatched('子', 'sky') }">
              <!-- 일지 표시 -->
              <span class="char-myfive-box">
                <div class="char-main" :class="getCharStyle('子', 'earth', 'wed', 'bottom')">
                  {{ getDisplayText('子', 'char', 'earth') }}
                </div>
                <VTooltip
                  v-if="isCharActive('子', 'earth', 'wed', 'bottom')"
                  activator="parent"
                  location="top"
                  ><div class="nm-tooltip">{{ getTooltipText('子') }}</div></VTooltip
                >
                <div class="char-1" :class="getChar3Style('子', 'earth', 'wed', 'bottom')">
                  자수
                </div>
                <div class="char-2">음 中 음</div>
                <div
                  class="char-3"
                  v-if="getCharPos('子', 'earth')"
                  :class="getChar3Style('子', 'earth', 'wed', 'bottom')"
                >
                  {{ getCharPos('子', 'earth') }}
                </div>
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 목 -->
    <div class="txt-five-box st-03 st-thu">
      <div class="inner-center-box">
        <div class="txt-top" :class="{ twoActive: isMultipleCharsMatched(['甲', '乙'], 'sky') }">
          <div class="al-left-box" :class="{ active: isCharMatched('甲', 'sky') }">
            <div class="char-wrapper" :class="{ myActive: isCharMatched('甲', 'sky') }">
              <!-- "일간" 표시 -->
              <span class="char-myfive-box">
                <div class="char-main" :class="getCharStyle('甲', 'sky', 'thu', 'top')">
                  {{ getDisplayText('甲', 'char', 'sky') }}
                </div>
                <VTooltip
                  v-if="isCharActive('甲', 'sky', 'thu', 'top')"
                  activator="parent"
                  location="top"
                  ><div class="nm-tooltip">{{ getTooltipText('甲') }}</div></VTooltip
                >
                <div class="char-1" :class="getChar3Style('甲', 'sky', 'thu', 'top')">갑목</div>
                <div class="char-2">양 中 양</div>
                <div
                  class="char-3"
                  v-if="getCharPos('甲', 'sky')"
                  :class="getChar3Style('甲', 'sky', 'thu', 'top')"
                >
                  {{ getCharPos('甲', 'sky') }}
                </div>
              </span>
            </div>
          </div>
          <div class="al-right-box" :class="{ active: isCharMatched('乙', 'sky') }">
            <div class="char-wrapper" :class="{ myActive: isCharMatched('乙', 'sky') }">
              <!-- "일간" 표시 -->
              <span class="char-myfive-box">
                <div class="char-main" :class="getCharStyle('乙', 'sky', 'thu', 'top')">
                  {{ getDisplayText('乙', 'char', 'sky') }}
                </div>
                <VTooltip
                  v-if="isCharActive('乙', 'sky', 'thu', 'top')"
                  activator="parent"
                  location="top"
                  ><div class="nm-tooltip">{{ getTooltipText('乙') }}</div></VTooltip
                >
                <div class="char-1" :class="getChar3Style('乙', 'sky', 'thu', 'top')">을목</div>
                <div class="char-2">양 中 음</div>
                <div
                  class="char-3"
                  v-if="getCharPos('乙', 'sky')"
                  :class="getChar3Style('乙', 'sky', 'thu', 'top')"
                >
                  {{ getCharPos('乙', 'sky') }}
                </div>
              </span>
              <!-- 문자 표시 -->
            </div>
          </div>
        </div>
        <div class="txt-line-box"></div>
        <div class="txt-btm" :class="{ twoActive: isMultipleCharsMatched(['寅', '卯'], 'earth') }">
          <div class="al-left-box" :class="{ active: isCharMatched('寅', 'earth') }">
            <div class="char-wrapper" :class="{ myActive: isCharMatched('寅', 'earth') }">
              <!-- 일지 표시 -->
              <span class="char-myfive-box">
                <div class="char-main" :class="getCharStyle('寅', 'earth', 'thu', 'bottom')">
                  {{ getDisplayText('寅', 'char', 'earth') }}
                </div>
                <VTooltip
                  v-if="isCharActive('寅', 'earth', 'thu', 'bottom')"
                  activator="parent"
                  location="top"
                  ><div class="nm-tooltip">{{ getTooltipText('寅') }}</div></VTooltip
                >
                <div class="char-1" :class="getChar3Style('寅', 'earth', 'thu', 'bottom')">
                  인목
                </div>
                <div class="char-2">양 中 양</div>
                <div
                  class="char-3"
                  v-if="getCharPos('寅', 'earth')"
                  :class="getChar3Style('寅', 'earth', 'thu', 'bottom')"
                >
                  {{ getCharPos('寅', 'earth') }}
                </div>
              </span>
            </div>
          </div>
          <div class="al-right-box" :class="{ active: isCharMatched('卯', 'earth') }">
            <div class="char-wrapper" :class="{ myActive: isCharMatched('卯', 'earth') }">
              <!-- 일지 표시 -->
              <span class="char-myfive-box">
                <div class="char-main" :class="getCharStyle('卯', 'earth', 'thu', 'bottom')">
                  {{ getDisplayText('卯', 'char', 'earth') }}
                </div>
                <VTooltip
                  v-if="isCharActive('卯', 'earth', 'thu', 'bottom')"
                  activator="parent"
                  location="top"
                  ><div class="nm-tooltip">{{ getTooltipText('卯') }}</div></VTooltip
                >
                <div class="char-1" :class="getChar3Style('卯', 'earth', 'thu', 'bottom')">
                  묘목
                </div>
                <div class="char-2">양 中 음</div>
                <div
                  class="char-3"
                  v-if="getCharPos('卯', 'earth')"
                  :class="getChar3Style('卯', 'earth', 'thu', 'bottom')"
                >
                  {{ getCharPos('卯', 'earth') }}
                </div>
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 화 -->
    <div class="txt-five-box st-01 st-tue">
      <div class="inner-center-box">
        <div class="txt-top" :class="{ twoActive: isMultipleCharsMatched(['丙', '丁'], 'sky') }">
          <div class="al-left-box" :class="{ active: isCharMatched('丙', 'sky') }">
            <div class="char-wrapper" :class="{ myActive: isCharMatched('丙', 'sky') }">
              <!-- "일간" 표시 -->
              <span class="char-myfive-box">
                <div class="char-main" :class="getCharStyle('丙', 'sky', 'tue', 'top')">
                  {{ getDisplayText('丙', 'char', 'sky') }}
                </div>
                <VTooltip
                  v-if="isCharActive('丙', 'sky', 'tue', 'top')"
                  activator="parent"
                  location="top"
                  ><div class="nm-tooltip">{{ getTooltipText('丙') }}</div></VTooltip
                >
                <div class="char-1" :class="getChar3Style('丙', 'sky', 'tue', 'top')">병화</div>
                <div class="char-2">양 中 양</div>
                <div
                  class="char-3"
                  v-if="getCharPos('丙', 'sky')"
                  :class="getChar3Style('丙', 'sky', 'tue', 'top')"
                >
                  {{ getCharPos('丙', 'sky') }}
                </div>
              </span>
              <!-- 문자 표시 -->
            </div>
          </div>
          <div class="al-right-box" :class="{ active: isCharMatched('丁', 'sky') }">
            <div class="char-wrapper" :class="{ myActive: isCharMatched('丁', 'sky') }">
              <!-- "일간" 표시 -->
              <span class="char-myfive-box">
                <div class="char-main" :class="getCharStyle('丁', 'sky', 'tue', 'top')">
                  {{ getDisplayText('丁', 'char', 'sky') }}
                </div>
                <VTooltip
                  v-if="isCharActive('丁', 'sky', 'tue', 'top')"
                  activator="parent"
                  location="top"
                  ><div class="nm-tooltip">{{ getTooltipText('丁') }}</div></VTooltip
                >
                <div class="char-1" :class="getChar3Style('丁', 'sky', 'tue', 'top')">정화</div>
                <div class="char-2">양 中 음</div>
                <div
                  class="char-3"
                  v-if="getCharPos('丁', 'sky')"
                  :class="getChar3Style('丁', 'sky', 'tue', 'top')"
                >
                  {{ getCharPos('丁', 'sky') }}
                </div>
              </span>
              <!-- 문자 표시 -->
            </div>
          </div>
        </div>
        <div class="txt-line-box"></div>
        <div class="txt-btm" :class="{ twoActive: isMultipleCharsMatched(['巳', '午'], 'earth') }">
          <div class="al-left-box" :class="{ active: isCharMatched('巳', 'earth') }">
            <!-- 일지 추가 테스트 -->
            <div class="char-wrapper" :class="{ myActive: isCharMatched('巳', 'earth') }">
              <!-- 일지 표시 -->
              <span class="char-myfive-box">
                <div class="char-main" :class="getCharStyle('巳', 'earth', 'tue', 'bottom')">
                  {{ getDisplayText('巳', 'char', 'earth') }}
                </div>
                <VTooltip
                  v-if="isCharActive('巳', 'earth', 'tue', 'bottom')"
                  activator="parent"
                  location="top"
                  ><div class="nm-tooltip">{{ getTooltipText('巳') }}</div></VTooltip
                >
                <div class="char-1" :class="getChar3Style('巳', 'earth', 'tue', 'bottom')">
                  사화
                </div>
                <div class="char-2">양 中 양</div>
                <div
                  class="char-3"
                  v-if="getCharPos('巳', 'earth')"
                  :class="getChar3Style('巳', 'earth', 'tue', 'bottom')"
                >
                  {{ getCharPos('巳', 'earth') }}
                </div>
              </span>
            </div>
          </div>
          <div class="al-right-box" :class="{ active: isCharMatched('未', 'earth') }">
            <div class="char-wrapper" :class="{ myActive: isCharMatched('未', 'earth') }">
              <!-- 일지 표시 -->
              <span class="char-myfive-box">
                <div class="char-main" :class="getCharStyle('午', 'earth', 'tue', 'bottom')">
                  {{ getDisplayText('午', 'char', 'earth') }}
                </div>
                <VTooltip
                  v-if="isCharActive('午', 'earth', 'tue', 'bottom')"
                  activator="parent"
                  location="top"
                  ><div class="nm-tooltip">{{ getTooltipText('午') }}</div></VTooltip
                >
                <div class="char-1" :class="getChar3Style('午', 'earth', 'tue', 'bottom')">
                  오화
                </div>
                <div class="char-2">양 中 음</div>
                <div
                  class="char-3"
                  v-if="getCharPos('午', 'earth')"
                  :class="getChar3Style('午', 'earth', 'tue', 'bottom')"
                >
                  {{ getCharPos('午', 'earth') }}
                </div>
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<style lang="scss">
.pro-star-box.myfive-box .txt-mid span.txt-rd-big {
  display: none;
}

.char-myfive-box {
  position: relative;

  .char-main {
    color: #c2c2c2;
  }

  .char-1,
  .char-2,
  .char-3 {
    display: none;
    position: absolute;
    top: -30px;
    left: 50%;
    transform: translateX(-50%);
    padding: 2px 6px;
    flex-direction: row;
    align-items: center;
    gap: 2px;
    border-radius: 14px;
    text-align: center;
    font-family: Pretendard;
    font-size: 13px;
    font-style: normal;
    font-weight: 700;
    line-height: 18px;
    white-space: nowrap;
    &.active {
      display: inline-flex;
    }
  }

  .char-2 {
    top: -66px;
    display: none;
  }
  .char-3 {
    top: -66px;
    display: none;
  }
}

.st-thu .char-myfive-box .char-main.active {
  box-shadow: 10px 10px 10px 0 rgba(94, 159, 28, 0.2);
}
.st-tue .char-myfive-box .char-main.active {
  box-shadow: 10px 10px 10px 0 rgba(194, 53, 53, 0.2);
}
.st-sat .char-myfive-box .char-main.active {
  box-shadow: 10px 10px 10px 0 rgba(219, 152, 35, 0.2);
}
.st-fri .char-myfive-box .char-main.active {
  box-shadow: 10px 10px 10px 0 rgba(148, 148, 148, 0.2);
}
.st-wed .char-myfive-box .char-main.active {
  box-shadow: 10px 10px 10px 0 rgba(64, 64, 64, 0.2);
}

.pos-first .char-myfive-box .char-3.active {
  display: none;
}
.txt-btm {
  .char-myfive-box {
    .char-1 {
      top: auto;
      bottom: -30px;
    }
    .char-3 {
      top: auto;
      bottom: -66px;
    }
  }
}

.pro-star-box.myfive-box.third {
  .char-1 {
    display: none;
  }
  .char-3 {
    top: -36px;
  }
}

.pro-star-box.myfive-box.fourth {
  .char-1 {
    display: none;
  }
  .txt-top {
    .char-3 {
      top: -36px;
    }
  }
  .txt-btm {
    .char-3 {
      bottom: -36px;
    }
  }
}

.pro-star-box.myfive-box.fifth {
  .char-1 {
    display: none;
  }
  .char-2 {
    display: none;
  }
  .txt-top {
    .char-3 {
      display: inline-flex;
      top: -30px;
    }
  }
  .txt-btm {
    .char-3 {
      display: inline-flex;
      bottom: -30px;
    }
  }
}

.char-3.active.icon-people {
  &:before {
    content: '';
    display: block;
    position: absolute;
    top: -20px;
    left: 50%;
    transform: translateX(-50%);
    background-image: url('@manseImages/icon_my_fill.png');
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    width: 16px;
    height: 16px;
  }
}

// 오행별 색상 적용 - 더 간결하고 확장 가능한 방식
@each $element,
  $colors
    in (
      sat: (
        #db9823,
        #faf0dd,
      ),
      fri: (
        #949494,
        #f0f0f0,
      ),
      tue: (
        #c23535,
        #f6e0e0,
      ),
      wed: (
        #404040,
        #e2e2e2,
      ),
      thu: (
        #5e9f1c,
        #e7f1dd,
      )
    )
{
  .st-#{$element} .char-myfive-box {
    .char-main {
      &.active {
        width: 44px;
        height: 44px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: nth($colors, 1);
        color: #ffffff;
        border-radius: 10px;
      }
    }

    .char-1 {
      color: nth($colors, 1);
      background: rgba(nth($colors, 1), 0.1);
    }
    .char-2 {
      color: nth($colors, 2);
      background: rgba(nth($colors, 2), 0.1);
    }
    .char-3 {
      color: nth($colors, 1);
      background: rgba(nth($colors, 1), 0.1);
    }
  }
}

.third .txt-top.twoActive .al-left-box {
  &::before {
    content: '';
    position: absolute;
    top: -9px;
    left: -35px;
    width: 160px;
    height: 60px;
    border: 1px solid #ecc88a;
    display: block;
    border-radius: 10px;
  }
  &::after {
    content: '천간';
    position: absolute;
    top: 13px;
    left: -72px;
    color: var(--Gray-800, #333);
    text-align: center;
    font-family: Pretendard;
    font-size: 13px;
    font-style: normal;
    font-weight: 700;
    line-height: 18px; /* 138.462% */
  }
}
.third .st-thu .txt-top.twoActive .al-left-box {
  &::before {
    border-color: #5e9f1c;
    background: #e7f1dd;
  }
}
.third .st-tue .txt-top.twoActive .al-left-box {
  &::before {
    border-color: #c23535;
    background: #f6e0e0;
  }
}
.third .st-wed .txt-top.twoActive .al-left-box {
  &::before {
    border-color: #404040;
    background: #e2e2e2;
  }
}
.third .st-fri .txt-top.twoActive .al-left-box {
  &::before {
    border-color: #949494;
    background: #f4f4f4;
  }
}
.third .st-sat .txt-top.twoActive .al-left-box {
  &::before {
    border-color: #db9823;
    background: #faf0dd;
  }
}

.fourth .txt-btm.twoActive .al-left-box {
  &::before {
    content: '';
    position: absolute;
    top: -9px;
    left: -35px;
    width: 160px;
    height: 60px;
    border: 1px dashed#ecc88a;
    display: block;
    border-radius: 10px;
  }
  &::after {
    content: '지지';
    position: absolute;
    top: 13px;
    left: -72px;
    color: var(--Gray-800, #333);
    text-align: center;
    font-family: Pretendard;
    font-size: 13px;
    font-style: normal;
    font-weight: 700;
    line-height: 18px; /* 138.462% */
  }
}
.fourth .st-thu .txt-btm.twoActive .al-left-box {
  &::before {
    border-color: #5e9f1c;
    background: #e7f1dd;
  }
}
.fourth .st-tue .txt-btm.twoActive .al-left-box {
  &::before {
    border-color: #c23535;
    background: #f6e0e0;
  }
}
.fourth .st-wed .txt-btm.twoActive .al-left-box {
  &::before {
    border-color: #404040;
    background: #e2e2e2;
  }
}
.fourth .st-fri .txt-btm.twoActive .al-left-box {
  &::before {
    border-color: #949494;
    background: #f4f4f4;
  }
}
.fourth .st-sat .txt-btm.twoActive .al-left-box {
  &::before {
    border-color: #db9823;
    width: 270px;
    background: #faf0dd;
  }
}

// Vuetify tooltip 스타일 커스터마이즈
// overlay가 Teleport로 body 하위에 렌더되므로 전역 선택자로 지정
.v-overlay.v-tooltip .v-overlay__content {
  color: var(--gray-100-white, #fff);
  text-align: center;
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 18px;
  border-radius: 10px;
  background: #111;
  padding: 8px 12px;
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.25);
  position: relative;
  display: inline-block; /* 내용만큼만 */
  width: auto;
  height: auto !important; /* 강제로 내용 높이만 */
  max-width: 200px; /* 두 줄로 적당히 줄바꿈 */
  white-space: normal; /* 컨테이너는 일반 줄바꿈, 실제 줄바꿈은 내부 nm-tooltip에서 제어 */
  align-self: center; /* 부모 flex의 세로 늘어짐 방지 */
}
.v-overlay.v-tooltip .v-overlay__content::after {
  content: '';
  position: absolute;
  bottom: -6px;
  left: 50%;
  transform: translateX(-50%) rotate(45deg);
  width: 10px;
  height: 10px;
  background: #111;
  box-shadow: 2px 2px 4px rgba(0, 0, 0, 0.2);
}
.v-overlay.v-tooltip .v-overlay__content .nm-tooltip {
  white-space: pre-line; // \n 줄바꿈 유지
  display: inline-block;
  text-align: center;
}
</style>
